import type {
	MPZP__ZoneInfo,
	ObszarUzupelnieniaZabudowy__ZoneInfo,
	ParcelGeometryInfo,
	ParcelInfo,
	ParcelPropertiesInfo,
	POG__ZoneInfo,
	PolygonDefinition,
	RingDefinition,
} from "@designboticteam/map";

function asString(value: unknown): string {
	return value === null || value === undefined ? "" : String(value);
}

function toRing(positions: GeoJSON.Position[]): RingDefinition {
	return positions.map(([lng, lat]) => ({ lng, lat }));
}

function toPolygons(geometry: GeoJSON.MultiPolygon): Array<PolygonDefinition> {
	return geometry.coordinates.map((rings) => ({
		outline: toRing(rings[0]),
		holes: rings.slice(1).map(toRing),
	}));
}

function parsePogZones(raw: unknown): POG__ZoneInfo[] {
	if (typeof raw !== "string" || raw === "") {
		return [];
	}

	const collection = JSON.parse(raw) as GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | null>;

	return collection.features.map((feature) => {
		const props = feature.properties ?? {};

		return {
			polygons: feature.geometry === null ? [] : toPolygons(feature.geometry),
			pole_geometryczne: asString(props.pole_geometryczne),
			pum: asString(props.pum),
			maksNadziemnaIntensywnoscZabudowy: asString(props.maksNadziemnaIntensywnoscZabudowy),
			maksUdzialPowierzchniZabudowy: asString(props.maksUdzialPowierzchniZabudowy),
			maksWysokoscZabudowy: asString(props.maksWysokoscZabudowy),
			maksWysokoscZabudowy_uom: asString(props.maksWysokoscZabudowy_uom),
			minUdzialPowierzchniBiologicznieCzynnej: asString(props.minUdzialPowierzchniBiologicznieCzynnej),
			obowiazujeOd: asString(props.obowiazujeOd),
			oznaczenie: asString(props.oznaczenie),
			symbol: asString(props.symbol),
		};
	});
}

function parseMpzpZones(raw: unknown): Array<MPZP__ZoneInfo> {
	if (typeof raw !== "string" || raw === "") {
		return [];
	}

	const collection = JSON.parse(raw) as GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | null>;

	return collection.features.map((feature) => {
		const props = feature.properties ?? {};

		return {
			polygons: feature.geometry === null ? [] : toPolygons(feature.geometry),
			pole_geometryczne: asString(props.pole_geometryczne),
			nrMpzp: asString(props.NR_MPZP),
			nazwa: asString(props.NAZWA),
			nrTerenu: asString(props.NR_TERENU),
			symbol: asString(props.SYMBOL),
			przeznaczenie: asString(props.PRZEZNACZENIE),
			kolor: asString(props.KOLOR),
			dataUpr: asString(props.DATA_UPR),
			skala: asString(props.SKALA),
			uchwala: asString(props.UCHWALA),
			terenLink: asString(props.TEREN_LINK),
			uchwalaLink: asString(props.UCHWALA_LINK),
			legendaLink: asString(props.LEGENDA_LINK),
			linkRys: asString(props.LINK_RYS),
			pum: asString(props.pum),
			przeznaczenieSymbol: asString(props.przeznaczenieSymbol),
			przeznaczenieOpis: asString(props.przeznaczenieOpis),
			kategoria: asString(props.kategoria),
			powierzchniaTerenuHa: asString(props.powierzchniaTerenuHa),
			intensywnoscMax: asString(props.intensywnoscMax),
			intensywnoscMin: asString(props.intensywnoscMin),
			powZabudowyMaxPct: asString(props.powZabudowyMaxPct),
			wysokoscMaxM: asString(props.wysokoscMaxM),
			powBiolCzynnaMinPct: asString(props.powBiolCzynnaMinPct),
			stawkaPct: asString(props.stawkaPct),
			wazneOd: asString(props.wazneOd),
			zrodloSilnik: asString(props.zrodloSilnik),
			zlaczenie: asString(props.zlaczenie),
			maParametry: asString(props.maParametry),
			zweryfikowaneRecznie: asString(props.zweryfikowaneRecznie),
		};
	});
}

function parseObszarUzupelnieniaZabudowyZones(raw: unknown): Array<ObszarUzupelnieniaZabudowy__ZoneInfo> {
	if (typeof raw !== "string" || raw === "") {
		return [];
	}

	const collection = JSON.parse(raw) as GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | null>;

	return collection.features.map((feature) => {
		const props = feature.properties ?? {};

		return {
			polygons: feature.geometry === null ? [] : toPolygons(feature.geometry),
			pole_geometryczne: asString(props.pole_geometryczne),
			oznaczenie: asString(props.oznaczenie),
			nazwa: asString(props.nazwa),
			symbol: asString(props.symbol),
		};
	});
}

function parseParcelPropertiesInfo(properties: GeoJSON.GeoJsonProperties): ParcelPropertiesInfo | null {
	const id = asString(properties?.parcel__id_dzialki);

	if (id === "") {
		return null;
	}

	return {
		id,
		numer: asString(properties?.parcel__numer_dzialki),
		gmina: asString(properties?.parcel__nazwa_gminy),
		obreb: asString(properties?.parcel__nazwa_obrebu),
		pole_geometryczne: asString(properties?.parcel__pole_geometryczne),
		pole_ewidencyjne: asString(properties?.parcel__pole_ewidencyjne),
		grupa_rejestrowa: asString(properties?.parcel__grupa_rejestrowa),
		klasouzytki_egib: asString(properties?.parcel__klasouzytki_egib),
		data_publikacji: asString(properties?.parcel__data_publikacji),
		mpzp_przeznaczenie: asString(properties?.mpzp__przeznaczenie),
		pog_pum: asString(properties?.pog__pum),
		mpzp_pum: asString(properties?.mpzp__pum),
		budynki_liczba: asString(properties?.parcel__budynki_liczba),
		obszar_uzupelnienia_zabudowy_oznaczenie: asString(properties?.obszar_uzupelnienia_zabudowy__oznaczenie),
	};
}

function parseParcelGeometryInfo({ geometry, properties }: GeoJSON.Feature<GeoJSON.MultiPolygon>): ParcelGeometryInfo {
	return {
		polygons: toPolygons(geometry),
		pog_zones: parsePogZones(properties?.pog__zones),
		mpzp_zones: parseMpzpZones(properties?.mpzp__zones),
		obszar_uzupelnienia_zabudowy_zones: parseObszarUzupelnieniaZabudowyZones(properties?.obszar_uzupelnienia_zabudowy__zones),
		zabytki: asString(properties?.parcel__zabytki),
	};
}

function parseParcelInfo(feature: GeoJSON.Feature<GeoJSON.MultiPolygon>): ParcelInfo | null {
	const propertiesInfo = parseParcelPropertiesInfo(feature.properties);

	if (propertiesInfo === null) {
		return null;
	}

	return { ...propertiesInfo, ...parseParcelGeometryInfo(feature) };
}

export { parseParcelGeometryInfo, parseParcelInfo, parseParcelPropertiesInfo };
